import { ENotice, ERef, EUser, FirebaseTimestamp } from '.';
import { EEdition } from './eedition';
import { PublicationIssue } from './publicationIssue';

export enum RunStatusType {
  PENDING = 'pending',
  /**
   * OCR will not verify this run because its notice's affidavit settings bypass
   * automatic verification, so a person verifies it instead. Unlike SKIPPED,
   * the run still needs verifying: every verification queue must include it,
   * or nothing will ever verify it.
   *
   * If bypass is turned off, a run returns to PENDING only while its
   * publication date has not passed. OCR is triggered by e-edition uploads and
   * a sweep of recent dates, so an older run moved to PENDING could be missed
   * by both and never verified.
   */
  BYPASSED_OCR = 'bypassed OCR',
  /**
   * Nothing will OCR or manually verify this run, because its publisher is not
   * on automated affidavits. Distinct from DISABLED, which means the
   * publication date was removed from the notice. A run enters this state when
   * it is created or when a disabled run is revived, and only ever moves back
   * to PENDING.
   */
  SKIPPED = 'skipped',
  /**
   * The run's notice already has an affidavit, so nothing will OCR or manually
   * verify it, whatever attached that affidavit. Nothing clears a notice's
   * affidavit, so this status has no route back to PENDING.
   */
  AFFIDAVIT_COMPLETE = 'affidavit complete',
  MANUALLY_VERIFIED = 'manually verified',
  OCR_VERIFICATION_SUCCEEDED = 'verified via OCR',
  OCR_VERIFICATION_FAILED = 'OCR verification failed',
  UNVERIFIABLE = 'unverifiable',
  CANCELLED = 'cancelled',
  DISABLED = 'disabled'
}

export const verifiedRunStatuses = [
  RunStatusType.MANUALLY_VERIFIED,
  RunStatusType.OCR_VERIFICATION_SUCCEEDED
] as const;
export type VerifiedRunStatus = (typeof verifiedRunStatuses)[number];

/**
 * The status a run holds before anything has verified it, while its publication
 * date is on the notice. Which one applies is resolved by
 * `getActiveRunStatusForNotice`.
 */
export type ActiveRunStatus =
  | typeof RunStatusType.PENDING
  | typeof RunStatusType.BYPASSED_OCR
  | typeof RunStatusType.SKIPPED
  | typeof RunStatusType.AFFIDAVIT_COMPLETE;

/**
 * Run statuses from which OCR verification can still run against an e-edition.
 */
export const ocrVerifiableRunStatuses = [
  RunStatusType.PENDING,
  RunStatusType.OCR_VERIFICATION_FAILED,
  RunStatusType.UNVERIFIABLE
] as const;
export type OcrVerifiableRunStatus = (typeof ocrVerifiableRunStatuses)[number];

/**
 * Run statuses from which a person can still record a verification decision.
 */
export const manuallyVerifiableRunStatuses = [
  RunStatusType.PENDING,
  RunStatusType.BYPASSED_OCR,
  RunStatusType.OCR_VERIFICATION_FAILED,
  RunStatusType.UNVERIFIABLE
] as const;
export type ManuallyVerifiableRunStatus =
  (typeof manuallyVerifiableRunStatuses)[number];

export type Run = {
  publicationIssue: ERef<PublicationIssue>;

  /**
   * The date of publication in format YYYY-MM-DD
   * Should be identical to the analogous property on the publication issue,
   * copied here to make it easier to query for runs by date
   */
  publicationDate: string;
  notice: ERef<ENotice>;
  status: RunStatusType;
  statusChangedAt?: FirebaseTimestamp;
  statusChangedBy?: ERef<EUser>;
  unverifiableReason?: string;
  eedition?: ERef<EEdition>;
  modifiedAt?: FirebaseTimestamp;

  /**
   * Run verification initiated events the OCR cron has not finished. An entry
   * outlives its event if the cron dies on the event's final attempt, so
   * readers also bound it by `lastOcrCheckQueuedAt`.
   */
  ocrQueuedEventIds?: string[];
  lastOcrCheckQueuedAt?: FirebaseTimestamp;
};

/**
 * A run awaiting manual verification, as listed in the admin Run Verification
 * table. Timestamps are ISO strings so the row survives JSON transport.
 */
export type RunNeedingVerification = {
  runId: string;
  noticeId: string;
  publicationDate: string;
  status: RunStatusType;
  statusChangedAt: string | null;
  publisherId: string | null;
  publisherName: string | null;
  parentId: string | null;
  parentName: string | null;
};

export type EEditionForVerification = {
  eeditionId: string;
  fileName: string;
  url: string;
};

export type RunForVerification = {
  runId: string;
  noticeId: string;
  publicationDate: string;
  status: RunStatusType;
  publisherId: string;
  publisherName: string;
  parentId: string | null;
  parentName: string | null;
  noticeImageUrl: string | null;
  noticePdfUrl: string | null;
  noticeText: string;
  /**
   * One per storage path, in display order: the one OCR found the notice in,
   * then the rest OCR checked, then OCR-pending, then the rest. The OCR lists
   * below name these e-editions, each standing for every record on its storage
   * path.
   */
  eeditions: EEditionForVerification[];
  /**
   * Every e-edition OCR compared the notice against. For an OCR-failed run,
   * the notice was found in none of them.
   */
  ocrCheckedEEditionIds: string[];
  /**
   * E-editions queued for OCR against this run but not compared yet. Only an
   * OCR-verifiable run has any: the OCR job skips every other run.
   */
  ocrPendingEEditionIds: string[];
  /** Set only while the run's status is verified via OCR. */
  ocrFoundEEditionId: string | null;
};

/**
 * A subset of the Run type properties related to or dependent on the run's status
 */
export type RunStatusProps = Pick<
  Run,
  | 'status'
  | 'statusChangedAt'
  | 'statusChangedBy'
  | 'unverifiableReason'
  | 'eedition'
>;
